/**
 * App Component
 */
import {
  Component,
  HostListener,
  Injector,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { eq, get, includes, isEmpty, isString, size } from 'lodash';
import { AppInjector } from './app.injector';
import { lazyInject } from './shared/decorators';
import { PostMessageService, TokenSessionService } from './shared/services';
import { NavigateService } from './shared/services/navigate/navigate.service';
import { E2E_ATTRIBUTES } from './shared/types/enums/e2e-attributes.enum';
import { I18n } from '@app/i18n/strings-keys';
import { appModeInfo } from './shared/utils/app.utils';

/**
 * App Component
 * Top Level Component
 */
// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xea-app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.style.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AppComponent implements OnInit {
  @lazyInject(NavigateService) public navigateService: NavigateService;

  public MAIN_CONTEXT_E2E_ATTRIBUTE: string = E2E_ATTRIBUTES.MAIN_CONTEXT;

  public refType = 'host';

  @HostListener('window:message', ['$event'])
  public iframeMessageListener(event: MessageEvent): void {
    if (!event || !event?.data) {
      return;
    }
    try {
      const data = event.data;

      if (data?.terminalId) {
        this.onTerminalIdReceived(data);

        return;
      }

      let token: string = '';
      const checkTokenFormat = (checkToken: string): boolean =>
        includes(checkToken, '.') && eq(size(checkToken.split('.')), 3);

      if (isString(data) && checkTokenFormat(data)) {
        token = data;

        this.tokenSessionService.token$.next(token);
      } else {
        const { isDev } = appModeInfo();

        // In dev mode, ignore incorrect token format to avoid interrupting dev flow
        if (isDev) {
          return;
        }

        this.router.navigateByUrl('error', {
          state: {
            message: I18n.ErrorStrings.IncorrectUrl,
          },
        });
      }
    } catch {}
  }

  public constructor(
    protected injector: Injector,
    private router: Router,
    private postMessageService: PostMessageService,
    private tokenSessionService: TokenSessionService,
  ) {
    AppInjector.injector = this.injector;
  }

  public ngOnInit(): void {
    // Request remote context (terminalId & token) from host/iframe early
    this.postMessageService.sendRawMessage('requestToken');
    this.postMessageService.sendRawMessage('requestTerminalInfo');

    const { isDev, devToken } = appModeInfo();

    if (isDev && devToken) {
      setTimeout(() => {
        if (!isEmpty(this.tokenSessionService.token)) {
          return;
        }

        // send dev token to self window for testing purpose
        window.postMessage(devToken, window.location.origin);
      }, 1000);
    }
  }

  public onTerminalIdReceived(data: string | { terminalId: string }) {
    const terminalId = get(data, 'terminalId');

    if (!isEmpty(terminalId)) {
      this.tokenSessionService.terminalId$.next(terminalId);

      return;
    }
  }
}
